import React, { Suspense, useMemo, useRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import Media from 'react-media';
import { memberId } from '@trello/session-cookie';

import { HeaderTestIds } from '@trello/test-ids';
import classNames from 'classnames';

import { TrelloStorage } from '@trello/storage';
import { getNotificationFilterStateKey } from 'app/src/components/NotificationsMenu/notificationsMenuState';
import {
  getNotificationSeenStateGroupCount,
  getNotificationSeenStateKey,
} from 'app/src/components/NotificationsMenu/notificationSeenState';
// eslint-disable-next-line no-restricted-imports
import { setSeenUnreadNotificationsAction } from 'app/gamma/src/modules/state/models/notifications';
// eslint-disable-next-line no-restricted-imports
import { loadNotificationGroups } from 'app/gamma/src/modules/loaders/load-notifications';
import { ScreenBreakpoints } from 'app/src/components/Responsive';
import { CrossProductSearchWrapper } from 'app/src/components/CrossProductSearch';
import { QuickSwitcher } from 'app/src/components/QuickSwitcher';
import { useRouteId, RouteId } from '@trello/routes';
import { NotificationsMenuButton } from 'app/src/components/NotificationsMenu';
import { DesktopSettingsButton } from './DesktopSettingsButton';
import { ChannelSwitcherButton } from './ChannelSwitcherButton';
import { EnterpriseMenuButton } from './EnterpriseMenuButton';
import { HeaderLogo } from './HeaderLogo';
import { InfoMenuButton } from './InfoMenuButton';
import { MemberMenuButton } from './MemberMenuButton';
import { HeaderButton } from './HeaderButton';
import { ApplicationSwitcherIcon } from '@trello/nachos/icons/application-switcher';
import { useFeatureFlag } from '@trello/feature-flag-client';

import styles from './AuthenticatedHeader.less';
import { isDesktop, isTouch } from '@trello/browser';
import { useLazyComponent } from '@trello/use-lazy-component';
import { ChunkLoadErrorBoundary } from 'app/src/components/ChunkLoadErrorBoundary';
import { noop } from 'app/src/noop';
import { HeaderPrimaryNavigationItems } from 'app/src/components/HeaderPrimaryNavigationItems';
import { useAuthenticatedHeaderQuery } from './AuthenticatedHeaderQuery.generated';

import { EnterpriseHeaderLogo } from './EnterpriseHeaderLogo';
import { notificationsPaneState } from 'app/src/components/NotificationsMenu/notificationsPaneState';

export const AuthenticatedHeader: React.FunctionComponent = () => {
  const { data } = useAuthenticatedHeaderQuery({
    variables: { memberId: memberId || 'me' },
  });
  const member = data?.member;
  const dispatch = useDispatch();

  useEffect(() => {
    const onChangeNotificationSeenState = ({ key }: { key: string }) => {
      if (key === getNotificationSeenStateKey()) {
        dispatch(
          setSeenUnreadNotificationsAction(
            getNotificationSeenStateGroupCount(),
          ),
        );
      }
    };

    TrelloStorage.listenSyncedAcrossBrowser(onChangeNotificationSeenState);
    return () => {
      TrelloStorage.unlisten(onChangeNotificationSeenState);
    };
  }, [dispatch]);

  useEffect(() => {
    const onChangeNotificationFilterState = ({ key }: { key: string }) => {
      if (key === getNotificationFilterStateKey()) {
        notificationsPaneState.setValue({
          notificationGroupsStale: true,
        });
        dispatch(loadNotificationGroups());
      }
    };
    TrelloStorage.listenSyncedAcrossBrowser(onChangeNotificationFilterState);
    return () => {
      TrelloStorage.unlisten(onChangeNotificationFilterState);
    };
  }, [dispatch]);

  const AtlassianAppSwitcherButton = useLazyComponent(
    () =>
      import(
        /* webpackChunkName: "atlassian-app-switcher-button" */ 'app/src/components/Header/AtlassianAppSwitcherButton'
      ),
    { namedImport: 'AtlassianAppSwitcherButton' },
  );

  const CrossFlowProvider = useLazyComponent(
    () =>
      import(
        /* webpackChunkName: "cross-flow-provider" */ 'app/src/components/CrossFlowProvider/CrossFlowProvider'
      ),
    { namedImport: 'CrossFlowProvider' },
  );

  const isNetworkRequestActivityEnabled = useFeatureFlag(
    'fep.developer_toolbar',
    false,
  );

  const isOrgDisplayInfoEnabled = useFeatureFlag(
    'enterprise.web.orgment-milestone-1',
    false,
  );

  const NetworkRequestActivityTool = useLazyComponent(
    () =>
      import(
        /* webpackChunkName: "network-request-activity-tool" */ 'app/src/components/NetworkRequestActivityTool'
      ),
    {
      namedImport: 'NetworkRequestActivityTool',
      preload: isNetworkRequestActivityEnabled,
    },
  );

  const Heartbeat = useLazyComponent(
    () =>
      import(
        /* webpackChunkName: "heartbeat" */ 'app/src/components/Heartbeat'
      ),
    {
      namedImport: 'Heartbeat',
      preload: !!member?.isAaMastered,
    },
  );

  const networkActivityTool = useMemo(
    () =>
      process.env.NODE_ENV !== 'production' &&
      isNetworkRequestActivityEnabled && <NetworkRequestActivityTool />,
    [NetworkRequestActivityTool, isNetworkRequestActivityEnabled],
  );

  const routeId = useRouteId();

  const headerLogoRef = useRef<HTMLAnchorElement>(null);
  const infoMenuButtonRef = useRef<HTMLButtonElement>(null);

  const renderAppSwitcherFallback = () => {
    return (
      <HeaderButton
        icon={<ApplicationSwitcherIcon />}
        onClick={noop}
        className={classNames(styles.headerButton, styles.appSwitcher)}
      />
    );
  };

  return (
    // Desktop injects styles based on this `id`, but we'll update it
    // soon to use the new `data-desktop-id`. We'd rather use isDesktop
    // and isMac here to add another class, but we need to take the app's zoom
    // factor in to account which is only available from within the
    // desktop context.
    <div
      className={classNames(styles.header, styles.noPrint)}
      id="header"
      data-test-id={HeaderTestIds.Container}
      data-desktop-id="header-inner"
    >
      {member?.isAaMastered && !isDesktop() && !isTouch() && (
        <Suspense fallback={renderAppSwitcherFallback()}>
          <ChunkLoadErrorBoundary fallback={null}>
            <CrossFlowProvider>
              <AtlassianAppSwitcherButton member={member} />
            </CrossFlowProvider>
          </ChunkLoadErrorBoundary>
        </Suspense>
      )}
      {isOrgDisplayInfoEnabled ? (
        <EnterpriseHeaderLogo />
      ) : (
        <HeaderLogo ref={headerLogoRef} />
      )}
      <div className={styles.leftSection}>
        <HeaderPrimaryNavigationItems />
      </div>
      <QuickSwitcher />
      <div className={styles.rightSection}>
        {routeId !== RouteId.SEARCH && (
          <CrossProductSearchWrapper focusRefOnDismiss={infoMenuButtonRef} />
        )}
        {!isDesktop() && (
          <Media query={ScreenBreakpoints.MediumMin}>
            <InfoMenuButton infoMenuButtonRef={infoMenuButtonRef} />
          </Media>
        )}
        <NotificationsMenuButton />
        {/*Don't show the Enterprise menu on tiny devices*/}
        <Media query="only screen and (min-width: 450px)">
          <EnterpriseMenuButton />
        </Media>
        <Media query={ScreenBreakpoints.MediumMin}>
          {(matches: boolean) =>
            matches && (
              <div className={styles.flexContainer}>
                <ChannelSwitcherButton />
              </div>
            )
          }
        </Media>
        <MemberMenuButton />
        {isDesktop() && <DesktopSettingsButton />}
      </div>
      {networkActivityTool}
      <Suspense fallback={null}>
        <ChunkLoadErrorBoundary fallback={null}>
          {/* Locating this here until all Trello users are 100% migrated to Aa. */}
          {/* This can be moved to TrelloOnline once the migration is complete. */}
          <Heartbeat isAaMastered={!!member?.isAaMastered} />
        </ChunkLoadErrorBoundary>
      </Suspense>
    </div>
  );
};
